import { supabase } from "../lib/supabaseClient";
import { OperationInsertRow, DailyAggregateRow } from "../types/operations";
import { HistoryMachineType } from "../constants/historyMachineTypeFilters";

export async function insertOperations(
  rows: OperationInsertRow[]
): Promise<number> {
  if (rows.length === 0) {
    return 0;
  }

  const { data, error } = await supabase
    .from("operations")
    .insert(rows)
    .select("id");

  if (error) {
    throw new Error(`Failed to insert operations: ${error.message}`);
  }

  return data ? data.length : 0;
}

// =============================================
// Fetch aggregated daily data from the
// daily_aggregates view for the past N days.
//
// The cutoff is calculated from today's date
// in UTC so results are consistent regardless
// of when during the day the page is loaded.
// =============================================
export async function fetchRecentDailyAggregates(
  numberOfDays: number
): Promise<DailyAggregateRow[]> {
  const cutoffDate = new Date();
  cutoffDate.setUTCDate(cutoffDate.getUTCDate() - numberOfDays + 1);
  cutoffDate.setUTCHours(0, 0, 0, 0);
  const cutoffIso = cutoffDate.toISOString().split("T")[0];

  const { data, error } = await supabase
    .from("daily_aggregates")
    .select("date, machine_type, machine_number, shift, total_parts, total_minutes")
    .gte("date", cutoffIso)
    .order("date", { ascending: true });

  if (error) {
    throw new Error(`Failed to fetch daily aggregates: ${error.message}`);
  }

  if (!data) {
    return [];
  }

  return data as DailyAggregateRow[];
}

// =============================================
// Fetch aggregated daily data from the
// daily_aggregates view for a specific date range,
// narrowed to a single machine type.
//
// startDate and endDate must be ISO 8601 date
// strings (e.g. "2026-04-28"). Both endpoints
// are inclusive. Rows are ordered chronologically.
//
// machineType must be one of the three Postgres
// enum values: "CNC", "Edge", or "Saw".
// =============================================
export async function fetchDailyAggregatesForDateRange(
  startDate: string,
  endDate: string,
  machineType: HistoryMachineType
): Promise<DailyAggregateRow[]> {
  const { data, error } = await supabase
    .from("daily_aggregates")
    .select("date, machine_type, machine_number, shift, total_parts, total_minutes")
    .gte("date", startDate)
    .lte("date", endDate)
    .eq("machine_type", machineType)
    .order("date", { ascending: true });

  if (error) {
    throw new Error(`Failed to fetch daily aggregates for date range: ${error.message}`);
  }

  if (!data) {
    return [];
  }

  return data as DailyAggregateRow[];
}
